/**
 * The one and only time a set of backup codes is visible.
 *
 * They are stored hashed, so there is no "show them again" — which is exactly
 * why this panel has to work harder than a list usually would: copy and
 * download are there because "write these down" is advice most people will not
 * take, and the acknowledgement is there so the codes cannot vanish behind a
 * closed dialog nobody read.
 */

import React from 'react';
import { Button, Alert, AlertTitle, AlertDescription } from '@lognai/synapse-design-system-v2';

export interface BackupCodesProps {
  codes: string[];
  /** Shown above the list; the wording differs for a first enrol vs a reissue. */
  title: string;
  description: string;
  onDone: () => void;
  doneLabel?: string;
}

export const BackupCodes: React.FC<BackupCodesProps> = ({
  codes,
  title,
  description,
  onDone,
  doneLabel = 'I have saved these codes',
}) => {
  const [copied, setCopied] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);

  const asText = codes.join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused outright (insecure context, denied
      // permission). The codes are on screen either way, so this is not worth
      // an error — but it must not silently claim success.
      setCopied(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([`${asText}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lognai-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>{description}</AlertDescription>
      </Alert>

      <ul className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/40 p-4">
        {codes.map((code) => (
          <li key={code} className="font-mono text-sm tracking-wider text-foreground">
            {code}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy codes'}
        </Button>
        <Button type="button" variant="outline" onClick={handleDownload}>
          Download
        </Button>
      </div>

      <label className="flex items-start gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-border"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span>
          I have saved these codes somewhere safe. I understand they will not be shown again.
        </span>
      </label>

      <Button type="button" disabled={!acknowledged} onClick={onDone}>
        {doneLabel}
      </Button>
    </div>
  );
};
